import { useCallback, useRef, useState } from 'react'

export interface BoundingBox {
  x: number       // normalized 0-1
  y: number       // normalized 0-1
  width: number   // normalized 0-1
  height: number  // normalized 0-1
}

export interface Annotation {
  id?: string
  field_id: string
  field_name?: string
  page_number: number
  bounding_box: BoundingBox
  selected_text?: string
}

interface AnnotationLayerProps {
  canvasWidth: number
  canvasHeight: number
  currentPage: number
  annotations: Annotation[]
  annotationMode: boolean
  onAnnotationDrawn: (box: BoundingBox) => void
  onAnnotationClick?: (annotation: Annotation) => void
  /** A pending draft bounding box to keep visible while user fills the form */
  draftBox?: BoundingBox | null
}

interface DrawState {
  startX: number
  startY: number
  currentX: number
  currentY: number
  isDrawing: boolean
}

// Deterministic color assignment per field
const fieldColorCache: Record<string, string> = {}
let fieldColorIndex = 0
function getFieldColor(fieldId: string): string {
  if (!fieldColorCache[fieldId]) {
    const hue = (fieldColorIndex * 137) % 360
    fieldColorCache[fieldId] = `hsl(${hue}, 70%, 50%)`
    fieldColorIndex++
  }
  return fieldColorCache[fieldId]
}

export function AnnotationLayer({
  canvasWidth,
  canvasHeight,
  currentPage,
  annotations,
  annotationMode,
  onAnnotationDrawn,
  onAnnotationClick,
  draftBox,
}: AnnotationLayerProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [drawState, setDrawState] = useState<DrawState | null>(null)

  const getRelativeCoords = useCallback(
    (e: MouseEvent | React.MouseEvent): { x: number; y: number } => {
      const rect = overlayRef.current!.getBoundingClientRect()
      return {
        x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
        y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
      }
    },
    []
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!annotationMode) return
      e.preventDefault()
      const { x, y } = getRelativeCoords(e)
      setDrawState({ startX: x, startY: y, currentX: x, currentY: y, isDrawing: true })
    },
    [annotationMode, getRelativeCoords]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!drawState?.isDrawing) return
      const { x, y } = getRelativeCoords(e)
      setDrawState((prev) => (prev ? { ...prev, currentX: x, currentY: y } : null))
    },
    [drawState, getRelativeCoords]
  )

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!drawState?.isDrawing) return
      const { x, y } = getRelativeCoords(e)

      const minX = Math.min(drawState.startX, x)
      const minY = Math.min(drawState.startY, y)
      const width = Math.abs(x - drawState.startX)
      const height = Math.abs(y - drawState.startY)

      // Only emit if box is large enough (>1% of canvas in each dimension)
      if (width > 0.01 && height > 0.01) {
        onAnnotationDrawn({ x: minX, y: minY, width, height })
      }

      setDrawState(null)
    },
    [drawState, getRelativeCoords, onAnnotationDrawn]
  )

  const pageAnnotations = annotations.filter((a) => a.page_number === currentPage)

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: canvasWidth,
        height: canvasHeight,
        pointerEvents: annotationMode ? 'auto' : 'none',
        cursor: annotationMode ? 'crosshair' : 'default',
        userSelect: annotationMode ? 'none' : 'auto',
        zIndex: 10,
        touchAction: 'pan-y',        // allow scroll-through on touch
        overscrollBehavior: 'auto',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onWheel={(e) => {
        // Let scroll events pass through to the parent scroll container
        e.currentTarget.style.pointerEvents = 'none'
        requestAnimationFrame(() => {
          if (overlayRef.current && annotationMode) {
            overlayRef.current.style.pointerEvents = 'auto'
          }
        })
      }}
    >
      <svg
        width={canvasWidth}
        height={canvasHeight}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {/* Existing annotations */}
        {pageAnnotations.map((ann, idx) => {
          const color = getFieldColor(ann.field_id)
          const x = ann.bounding_box.x * canvasWidth
          const y = ann.bounding_box.y * canvasHeight
          const w = ann.bounding_box.width * canvasWidth
          const h = ann.bounding_box.height * canvasHeight
          return (
            <g
              key={ann.id || idx}
              onClick={(e) => {
                e.stopPropagation()
                onAnnotationClick?.(ann)
              }}
              style={{ cursor: 'pointer', pointerEvents: 'all' }}
            >
              <rect
                x={x}
                y={y}
                width={w}
                height={h}
                fill={color}
                fillOpacity={0.2}
                stroke={color}
                strokeWidth={2}
              />
              {ann.field_name && (
                <text
                  x={x + 4}
                  y={y - 4}
                  fill={color}
                  fontSize={11}
                  fontWeight="bold"
                >
                  {ann.field_name}
                </text>
              )}
            </g>
          )
        })}

        {/* Currently drawing */}
        {drawState?.isDrawing && (
          <rect
            x={Math.min(drawState.startX, drawState.currentX) * canvasWidth}
            y={Math.min(drawState.startY, drawState.currentY) * canvasHeight}
            width={Math.abs(drawState.currentX - drawState.startX) * canvasWidth}
            height={Math.abs(drawState.currentY - drawState.startY) * canvasHeight}
            fill="rgba(59,130,246,0.2)"
            stroke="#3b82f6"
            strokeWidth={2}
            strokeDasharray="4 2"
          />
        )}

        {/* Draft box — stays visible while user fills the annotation form */}
        {draftBox && !drawState?.isDrawing && (
          <rect
            x={draftBox.x * canvasWidth}
            y={draftBox.y * canvasHeight}
            width={draftBox.width * canvasWidth}
            height={draftBox.height * canvasHeight}
            fill="rgba(59,130,246,0.15)"
            stroke="#3b82f6"
            strokeWidth={2}
            strokeDasharray="6 3"
          />
        )}
      </svg>
    </div>
  )
}
